import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
} from "recharts";
import { Lock } from "lucide-react";

// Hypothèses: 1 réseau signé par trimestre, 40 000 € volume/mois/réseau,
// marge nette 2%, niveau Business Partner 25% = 200 €/mois/réseau.
const VOLUME = 40000;
const MARGE = 0.02;
const BP_PCT = 0.25;
const MONTHLY_PER_NET = VOLUME * MARGE * BP_PCT; // 200

function buildSeries() {
    const data = [];
    let networks = 0;
    let cumulative = 0;
    for (let m = 1; m <= 36; m++) {
        if ((m - 1) % 3 === 0) networks += 1; // nouveau réseau chaque trimestre
        const monthly = networks * MONTHLY_PER_NET;
        cumulative += monthly;
        data.push({ month: m, monthly, cumulative, networks });
    }
    return data;
}

function fmtEUR(n) {
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(n);
}

export default function Recurrence() {
    const data = useMemo(() => buildSeries(), []);
    const ref = useRef(null);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        // Chart renders immediately; IO kept as a safety net to retrigger
        // recharts animation if user scrolls back into view.
        if (!ref.current) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisible(true);
                }
            },
            { threshold: 0.05 },
        );
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div
            id="recurrence"
            className="relative mt-20 sm:mt-24"
            data-testid="recurrence-section"
        >
            <div className="relative">
                <div className="max-w-3xl">
                    <p className="fade-up eyebrow">
                        La puissance de la récurrence sur 3 ans
                    </p>
                    <h3
                        className="fade-up h-display text-2xl sm:text-3xl lg:text-[44px] font-bold mt-3"
                        style={{ animationDelay: "0.05s" }}
                        data-testid="recurrence-title"
                    >
                        Ce que tu construis{" "}
                        <span className="text-violet-glow">vraiment</span>.
                    </h3>
                    <p
                        className="fade-up mt-5 text-[17px] text-white/65 leading-relaxed"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Chaque réseau signé génère une commission mensuelle
                        récurrente. Les réseaux s'accumulent. La rente se
                        construit.
                    </p>
                </div>

                {/* Chart */}
                <div
                    ref={ref}
                    className="fade-up mt-12 relative rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] p-5 sm:p-8"
                    style={{ animationDelay: "0.15s" }}
                    data-testid="recurrence-chart"
                >
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div>
                            <p className="eyebrow">Projection 36 mois</p>
                            <p className="text-[14px] text-white/70 mt-1">
                                1 réseau signé par trimestre · volume moyen 40 000
                                € · Business Partner
                            </p>
                        </div>
                        <div className="flex items-center gap-5 text-[12px]">
                            <Legend
                                color="rgba(139,92,246,0.6)"
                                label="Commission mensuelle"
                            />
                            <Legend
                                color="#F59E0B"
                                label="Total cumulé"
                                line
                            />
                        </div>
                    </div>

                    <div className="h-[300px] sm:h-[360px]">
                        {visible && (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={data}
                                    margin={{
                                        top: 8,
                                        right: 12,
                                        left: 0,
                                        bottom: 8,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="vgrad"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#8B5CF6"
                                                stopOpacity={0.8}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#6C47FF"
                                                stopOpacity={0.05}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        stroke="rgba(255,255,255,0.06)"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="month"
                                        tick={{
                                            fill: "rgba(255,255,255,0.45)",
                                            fontSize: 11,
                                        }}
                                        ticks={[1, 6, 12, 18, 24, 30, 36]}
                                        tickFormatter={(m) => `M${m}`}
                                        axisLine={{
                                            stroke: "rgba(255,255,255,0.1)",
                                        }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tick={{
                                            fill: "rgba(255,255,255,0.45)",
                                            fontSize: 11,
                                        }}
                                        tickFormatter={(v) =>
                                            `${(v / 1000).toFixed(0)}k`
                                        }
                                        axisLine={false}
                                        tickLine={false}
                                        width={42}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        tick={{
                                            fill: "rgba(245,158,11,0.7)",
                                            fontSize: 11,
                                        }}
                                        tickFormatter={(v) =>
                                            `${(v / 1000).toFixed(0)}k`
                                        }
                                        axisLine={false}
                                        tickLine={false}
                                        width={42}
                                    />
                                    <Tooltip
                                        cursor={{
                                            stroke: "rgba(255,255,255,0.15)",
                                            strokeWidth: 1,
                                        }}
                                        contentStyle={{
                                            background: "#0A0A0F",
                                            border:
                                                "1px solid rgba(255,255,255,0.1)",
                                            borderRadius: 10,
                                            color: "#fff",
                                            fontSize: 12,
                                        }}
                                        labelFormatter={(m) => `Mois ${m}`}
                                        formatter={(value, name) => {
                                            const label =
                                                name === "monthly"
                                                    ? "Commission / mois"
                                                    : "Total cumulé";
                                            return [fmtEUR(value), label];
                                        }}
                                    />
                                    <ReferenceLine
                                        yAxisId="left"
                                        x={12}
                                        stroke="rgba(255,255,255,0.25)"
                                        strokeDasharray="3 3"
                                        label={{
                                            value: "1 an",
                                            fill: "rgba(255,255,255,0.5)",
                                            fontSize: 10,
                                            position: "top",
                                        }}
                                    />
                                    <ReferenceLine
                                        yAxisId="left"
                                        x={24}
                                        stroke="rgba(255,255,255,0.25)"
                                        strokeDasharray="3 3"
                                        label={{
                                            value: "2 ans",
                                            fill: "rgba(255,255,255,0.5)",
                                            fontSize: 10,
                                            position: "top",
                                        }}
                                    />
                                    <ReferenceLine
                                        yAxisId="left"
                                        x={36}
                                        stroke="rgba(245,158,11,0.5)"
                                        strokeDasharray="3 3"
                                        label={{
                                            value: "3 ans",
                                            fill: "#F59E0B",
                                            fontSize: 10,
                                            position: "top",
                                        }}
                                    />
                                    <Area
                                        yAxisId="left"
                                        type="stepAfter"
                                        dataKey="monthly"
                                        stroke="#8B5CF6"
                                        strokeWidth={2}
                                        fill="url(#vgrad)"
                                        isAnimationActive
                                        animationDuration={1400}
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="cumulative"
                                        stroke="#F59E0B"
                                        strokeWidth={2.5}
                                        dot={false}
                                        isAnimationActive
                                        animationDuration={1800}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Milestone cards */}
                <div className="mt-10 grid md:grid-cols-3 gap-5">
                    <Milestone
                        label="Après 1 an"
                        value="800 € / mois"
                        sub="4 réseaux actifs — 1 par trimestre"
                        testid="milestone-1y"
                    />
                    <Milestone
                        label="Après 2 ans"
                        value="1 600 € / mois"
                        sub="8 réseaux actifs — sans tout reprospecter"
                        testid="milestone-2y"
                    />
                    <Milestone
                        label="Après 3 ans"
                        value="2 400 € / mois"
                        sub="12 réseaux actifs — une rente construite"
                        highlight
                        testid="milestone-3y"
                    />
                </div>

                {/* Editorial */}
                <div className="mt-12 max-w-3xl">
                    <p className="text-[15.5px] text-white/70 leading-relaxed">
                        Ces chiffres sont basés sur 1 réseau signé par
                        trimestre, un volume moyen de 40 000 €/mois, niveau
                        Business Partner.
                    </p>
                    <p className="mt-5 text-[19px] sm:text-[22px] font-semibold text-white leading-snug h-display">
                        Tu n'as pas besoin de signer 50 réseaux.
                        <br />
                        Tu as besoin de signer{" "}
                        <span className="text-cap-violetSoft">les bons</span>,
                        régulièrement, et de les garder actifs.
                    </p>
                    <p className="mt-4 text-[15px] text-white/55 italic">
                        CapDistrib s'occupe du reste.
                    </p>
                </div>

                {/* Garantie */}
                <div
                    className="mt-10 inline-flex items-start gap-3 p-4 sm:p-5 rounded-2xl border border-cap-gold/30 bg-cap-gold/[0.06] max-w-3xl"
                    data-testid="recurrence-guarantee"
                >
                    <div className="w-9 h-9 rounded-full bg-cap-gold/15 ring-1 ring-cap-gold/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Lock size={16} className="text-cap-gold" />
                    </div>
                    <p className="text-[14.5px] text-white leading-relaxed">
                        <span className="font-semibold">
                            Commissions garanties sur la durée du contrat
                            partenaire.
                        </span>{" "}
                        <span className="text-white/70">
                            Tant que le réseau est actif, tu es commissionné.
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
}

function Milestone({ label, value, sub, highlight, testid }) {
    return (
        <div
            className={`relative glass p-7 ${
                highlight
                    ? "border-cap-gold/60 bg-cap-gold/[0.06] shadow-[0_24px_60px_-20px_rgba(245,158,11,0.4)]"
                    : ""
            }`}
            data-testid={testid}
        >
            <p
                className={`text-[10.5px] uppercase tracking-[0.22em] font-semibold ${
                    highlight ? "text-cap-gold" : "text-white/50"
                }`}
            >
                {label}
            </p>
            <p
                className={`dashboard-num mt-3 text-3xl sm:text-4xl font-bold leading-none ${
                    highlight ? "text-cap-gold" : "text-white"
                }`}
            >
                {value}
            </p>
            <p className="mt-3 text-[13.5px] text-white/65 leading-relaxed">
                {sub}
            </p>
        </div>
    );
}

function Legend({ color, label, line }) {
    return (
        <span className="inline-flex items-center gap-2 text-white/70">
            <span
                className={line ? "w-5 h-[2px]" : "w-3 h-3 rounded-sm"}
                style={{ background: color }}
            />
            {label}
        </span>
    );
}
